/**
 * AdminDuplicationAudit — Ecosystem Module Duplication Audit report.
 *
 * Route: /admin/audit/duplication (super-admin only)
 * Data: GET /api/admin/audit/service-duplication
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, FileWarning, CheckCircle2 } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

export default function AdminDuplicationAudit() {
  const q = useQuery({
    queryKey: ['dup-audit'],
    queryFn: async () => {
      const r = await axios.get(`${API}/api/admin/audit/service-duplication`, { headers: authH() });
      return r.data;
    },
  });

  const data = q.data || {};
  const findings = data.findings || [];
  const totals = data.totals_by_signal || [];

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100" data-testid="admin-dup-audit-page">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="flex items-center gap-3 text-3xl font-bold">
              <FileWarning className="h-7 w-7 text-amber-400" />
              Ecosystem Duplication Audit
            </h1>
            <p className="mt-1 text-sm text-slate-400">
              Modules still calling shared services directly instead of routing
              through the platform facades. Migrate these onto{' '}
              <code className="text-violet-300">document_service</code> +{' '}
              <code className="text-violet-300">system_notifications</code> to
              retire duplicated code.
            </p>
          </div>
          <Button
            data-testid="dup-audit-refresh"
            variant="outline"
            className="border-slate-700 bg-slate-900 hover:bg-slate-800"
            onClick={() => q.refetch()}
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Card className="border-slate-800 bg-slate-900/60">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-400">Files scanned</div>
              <div className="mt-2 text-3xl font-semibold" data-testid="stat-scanned">{data.files_scanned ?? '—'}</div>
            </CardContent>
          </Card>
          <Card className="border-amber-500/30 bg-amber-500/5">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-amber-300">With findings</div>
              <div className="mt-2 text-3xl font-semibold text-amber-200" data-testid="stat-findings">{data.files_with_findings ?? '—'}</div>
            </CardContent>
          </Card>
          <Card className="border-slate-800 bg-slate-900/60">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-400">Signal totals</div>
              <div className="mt-2 flex flex-wrap gap-1">
                {totals.map((t) => (
                  <Badge key={t.signal} variant="outline" className="border-slate-700 text-slate-300" data-testid={`total-${t.signal}`}>
                    {t.signal} · {t.count}
                  </Badge>
                ))}
                {totals.length === 0 && <span className="text-xs text-slate-500">Clean — no duplication found.</span>}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              Migration priority
            </CardTitle>
            <p className="text-xs text-slate-500">Ranked by total direct-service calls per file (highest first).</p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {q.isLoading && <div className="py-8 text-center text-slate-500">Loading…</div>}
            {!q.isLoading && findings.length === 0 && (
              <div className="flex items-center gap-2 py-8 text-emerald-300" data-testid="dup-empty">
                <CheckCircle2 className="h-5 w-5" />
                All routes now consume the platform facades. Zero duplication.
              </div>
            )}
            {findings.length > 0 && (
              <table className="w-full text-left text-sm" data-testid="findings-table">
                <thead>
                  <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-400">
                    <th className="py-2 pr-4">File</th>
                    <th className="py-2 pr-4">Signals</th>
                    <th className="py-2 pr-4 text-right">Total hits</th>
                  </tr>
                </thead>
                <tbody>
                  {findings.map((f) => (
                    <tr key={f.file} className="border-b border-slate-800/60" data-testid="finding-row">
                      <td className="py-2 pr-4 font-mono text-slate-300">{f.path}</td>
                      <td className="py-2 pr-4">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(f.signals).map(([k, v]) => (
                            <Badge key={k} variant="outline" className="border-slate-700 text-slate-300 text-xs">
                              {k} · {v}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-right font-semibold">{f.total_hits}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
